import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEditableDocument } from "../../contexts/EditableDocumentContext";
import AIStructureReview from "./AIStructureReview";
import LoadingSpinner from "../common/LoadingSpinner";
import apiClient from "../../services/apiClient";

const CreateDocumentModal = ({
  isOpen,
  onClose,
  onDocumentCreated,
  proposalId,
  templates = [],
}) => {
  const { createDocument, loading, error } = useEditableDocument();

  const [formData, setFormData] = useState({
    documentName: "",
    documentType: "proposal", // Changed from templateId
    useAIStructure: true, // Default to AI workflow
    customInstructions: "",
    autoPopulate: true, // Auto-populate content after structure
    generationMode: "holistic", // NEW: "holistic" or "section"
  });

  const [step, setStep] = useState(1); 
  // Steps: 1: Basic Info, 2: AI Structure Analysis, 3: Review Structure, 4: Content Generation
  
  const [aiSuggestedSections, setAiSuggestedSections] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [populationJobId, setPopulationJobId] = useState(null);
  const [populationStatus, setPopulationStatus] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [isProcessComplete, setIsProcessComplete] = useState(false);
  const pollingIntervalRef = useRef(null);

  // Guard to prevent double-execution in React StrictMode
  const isCreatingRef = useRef(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Clear any existing polling when modal opens
      if (pollingIntervalRef.current) {
        console.log("Modal opening - clearing any existing polling");
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      setFormData({
        documentName: "",
        documentType: "proposal",
        useAIStructure: true,
        customInstructions: "",
        autoPopulate: true,
        generationMode: "holistic",
      });
      setStep(1);
      setAiSuggestedSections([]);
      setIsAnalyzing(false);
      setIsCreating(false);
      setPopulationJobId(null);
      setPopulationStatus(null);
      setAnalysisError(null);
      setIsProcessComplete(false);
      isCreatingRef.current = false; // Reset guard when modal opens
    } else {
      // Clear polling when modal closes
      if (pollingIntervalRef.current) {
        console.log("Modal closing - clearing any active polling");
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isCreatingRef.current = false; // Reset guard when modal closes
    }
  }, [isOpen]);

  // Poll for content population status
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      console.log("Clearing existing polling interval");
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (populationJobId && isOpen && !isProcessComplete) {
      console.log(`Starting polling for job ${populationJobId}`);
      
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const response = await apiClient.get(
            `/api/editable-documents/population-status/${populationJobId}`
          );
          
          setPopulationStatus(response.data);
          
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            console.log(`Process completed with status: ${response.data.status}`);
            setIsProcessComplete(true);
            
            // Clear the interval immediately
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            if (response.data.status === 'completed') {
              // Document is ready, notify parent
              setTimeout(() => {
                // Clear all state before closing to prevent any lingering effects
                setPopulationJobId(null);
                setPopulationStatus(null);
                setIsProcessComplete(true);
                
                onDocumentCreated({ 
                  document_id: response.data.documentId,  // Changed from documentId to document_id
                  message: "Document created and populated successfully!" 
                });
                onClose();
              }, 1500);
            }
          }
        } catch (error) {
          console.error("Failed to check population status:", error);
          setIsProcessComplete(true);
          
          // Clear the interval on error
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }, 2000); // Poll every 2 seconds

      return () => {
        console.log("Cleaning up population status polling interval (useEffect cleanup)");
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [populationJobId, isOpen, isProcessComplete, onDocumentCreated, onClose]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        console.log("Component unmounting - cleaning up any active polling");
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAnalyzeRFP = async () => {
    if (!formData.documentName) {
      alert("Please enter a document name first");
      return;
    }

    setStep(2);
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const response = await apiClient.post("/api/editable-documents/analyze-rfp", {
        proposalId: parseInt(proposalId),
        documentType: formData.documentType,
        customInstructions: formData.customInstructions,
      });

      if (response.success) {
        console.log("API response:", response);
        console.log("Sections data:", response.data.sections);
        console.log("Sections type:", typeof response.data.sections);
        console.log("Is sections array:", Array.isArray(response.data.sections));
        
        // Ensure sections is an array
        let sections = response.data.sections;
        if (!Array.isArray(sections)) {
          console.error("Sections is not an array:", sections);
          throw new Error("Invalid sections format received from server");
        }
        
        setAiSuggestedSections(sections);
        setStep(3); // Move to review step
      } else {
        throw new Error(response.message || "Analysis failed");
      }
    } catch (error) {
      console.error("RFP analysis failed:", error);
      setAnalysisError(error.message || "Failed to analyze RFP documents. Please ensure RFP documents are uploaded.");
      setStep(1); // Go back to first step
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSectionsChange = (updatedSections) => {
    setAiSuggestedSections(updatedSections);
  };

  const handleConfirmStructure = async (finalSections) => {
    // Guard against double-execution (React StrictMode in dev)
    if (isCreatingRef.current) {
      console.log("⚠️ Document creation already in progress, skipping duplicate call");
      return;
    }

    isCreatingRef.current = true;
    setStep(4); // Move to content generation step
    setIsCreating(true);

    try {
      console.log(`🚀 Creating document with structure (${formData.generationMode} mode)...`);
      const response = await apiClient.post("/api/editable-documents/create-with-structure", {
        proposalId: parseInt(proposalId),
        documentName: formData.documentName,
        sections: finalSections,
        autoPopulate: formData.autoPopulate,
        generationMode: formData.generationMode, // NEW
      });

      if (response.success) {
        if (response.data.populationJobId) {
          // Content is being generated
          console.log(`✅ Document created with population job: ${response.data.populationJobId}`);
          setPopulationJobId(response.data.populationJobId);
          setPopulationStatus({
            status: 'in_progress',
            totalSections: finalSections.length,
            completedSections: 0,
            currentSection: finalSections[0]?.title || ''
          });
        } else {
          // Document created without content - normalize the response
          console.log("✅ Document created without auto-populate");
          onDocumentCreated({
            document_id: response.data.documentId,
            ...response.data
          });
          onClose();
        }
      }
    } catch (error) {
      console.error("Failed to create document:", error);
      setAnalysisError(error.message || "Failed to create document");
      setStep(3); // Go back to structure review
      setIsCreating(false);
      isCreatingRef.current = false; // Reset on error
    }
  };

  const handleTraditionalCreate = async () => {
    // Fallback to template-based creation if needed
    // This would use the old workflow with templates
    alert("Template-based creation is being phased out. Please use AI structure generation.");
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Name *
        </label>
        <input
          type="text"
          name="documentName"
          value={formData.documentName}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Technical Proposal v1"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type *
        </label>
        <select
          name="documentType"
          value={formData.documentType}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="proposal">Full Proposal</option>
          <option value="technical">Technical Approach</option>
          <option value="management">Management Plan</option>
          <option value="past_performance">Past Performance</option>
          <option value="pricing">Pricing Proposal</option>
          <option value="executive_summary">Executive Summary</option>
          <option value="custom">Custom Document</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Custom Instructions (Optional)
        </label>
        <textarea
          name="customInstructions"
          value={formData.customInstructions}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows="3"
          placeholder="e.g., Focus on technical requirements, emphasize cybersecurity experience..."
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="autoPopulate"
          name="autoPopulate"
          checked={formData.autoPopulate}
          onChange={handleInputChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="autoPopulate" className="text-sm text-gray-700">
          Auto-populate sections with AI-generated content
        </label>
      </div>

      {analysisError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
          {analysisError}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <FontAwesomeIcon icon="info-circle" className="mr-2" />
          AI will analyze your RFP documents to suggest an optimal document structure
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <FontAwesomeIcon 
        icon="spinner" 
        className="text-4xl text-blue-500 animate-spin mb-4" 
      />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Analyzing RFP Documents
      </h3>
      <p className="text-gray-600 text-center">
        AI is examining the RFP materials to determine<br />
        the optimal structure for your {formData.documentType}
      </p>
      <p className="text-sm text-gray-500 mt-4">This may take 30-60 seconds...</p>
    </div>
  );

  const renderStep3 = () => (
    <div>
      {/* Generation Mode Selector */}
      {formData.autoPopulate && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            <FontAwesomeIcon icon="cog" className="mr-2" />
            Content Generation Method
          </label>
          <div className="space-y-3">
            {/* Holistic Generation Option (Recommended) */}
            <div className="flex items-start">
              <input
                type="radio"
                id="mode-holistic"
                name="generationMode"
                value="holistic"
                checked={formData.generationMode === "holistic"}
                onChange={(e) => setFormData({ ...formData, generationMode: e.target.value })}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="mode-holistic" className="ml-3 cursor-pointer flex-1">
                <div className="flex items-center">
                  <span className="font-medium text-gray-900">
                    Holistic Generation
                  </span>
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Generates the entire document as one cohesive narrative. Better flow, no repetition, natural transitions between sections.
                </p>
              </label>
            </div>

            {/* Section-by-Section Option */}
            <div className="flex items-start">
              <input
                type="radio"
                id="mode-section"
                name="generationMode"
                value="section"
                checked={formData.generationMode === "section"}
                onChange={(e) => setFormData({ ...formData, generationMode: e.target.value })}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="mode-section" className="ml-3 cursor-pointer flex-1">
                <span className="font-medium text-gray-900">
                  Section-by-Section Generation
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Generates each section independently. Provides progress updates but may have redundant content between sections.
                </p>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* AI Structure Review Component */}
      <AIStructureReview
        sections={aiSuggestedSections}
        onSectionsChange={handleSectionsChange}
        onConfirm={handleConfirmStructure}
        onCancel={() => setStep(1)}
        isLoading={false}
      />
    </div>
  );

  const renderStep4 = () => {
    const progress = populationStatus
      ? Math.round((populationStatus.completedSections / populationStatus.totalSections) * 100)
      : 0;

    const isHolistic = formData.generationMode === 'holistic';

    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FontAwesomeIcon
          icon="magic"
          className="text-4xl text-purple-500 mb-4"
        />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {populationStatus?.status === 'completed'
            ? "Document Created Successfully!"
            : isHolistic
              ? "Generating Complete Document"
              : "Generating Document Content"}
        </h3>

        {populationStatus?.status === 'in_progress' && (
          <>
            <p className="text-gray-600 text-center mb-4">
              {isHolistic
                ? "AI is creating a cohesive proposal document with natural flow and no repetition..."
                : "AI is populating sections based on RFP requirements and relevant past proposals..."}
            </p>
            
            {!isHolistic && populationStatus && (
              <div className="w-full max-w-md mb-4">
                <div className="bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Section {populationStatus.completedSections} of {populationStatus.totalSections}
                </p>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Current: {populationStatus.currentSection}
                </p>
              </div>
            )}

            {isHolistic && (
              <div className="flex justify-center my-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}
          </>
        )}

        {populationStatus?.status === 'completed' && (
          <div className="text-green-600 text-center">
            <FontAwesomeIcon icon="check-circle" className="text-4xl mb-2" />
            <p>All sections have been populated!</p>
            <p className="text-sm text-gray-600 mt-2">Opening document editor...</p>
          </div>
        )}

        {populationStatus?.status === 'failed' && (
          <div className="text-red-600 text-center">
            <FontAwesomeIcon icon="exclamation-triangle" className="text-4xl mb-2" />
            <p>Content generation encountered an error</p>
            <p className="text-sm mt-2">{populationStatus.error}</p>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <FontAwesomeIcon icon="file-alt" className="mr-2" />
              Create New Document
            </h2>
            {step <= 3 && (
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon="times" size="lg" />
              </button>
            )}
          </div>
          {/* Step Indicator */}
          <div className="mt-4 flex items-center justify-between">
            <div className={`flex items-center ${step >= 1 ? 'text-white' : 'text-blue-300'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                step >= 1 ? 'border-white bg-white text-blue-600' : 'border-blue-300'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm">Basic Info</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-white' : 'bg-blue-300'}`} />
            <div className={`flex items-center ${step >= 2 ? 'text-white' : 'text-blue-300'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                step >= 2 ? 'border-white bg-white text-blue-600' : 'border-blue-300'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm">AI Analysis</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-white' : 'bg-blue-300'}`} />
            <div className={`flex items-center ${step >= 3 ? 'text-white' : 'text-blue-300'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                step >= 3 ? 'border-white bg-white text-blue-600' : 'border-blue-300'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm">Review</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 4 ? 'bg-white' : 'bg-blue-300'}`} />
            <div className={`flex items-center ${step >= 4 ? 'text-white' : 'text-blue-300'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                step >= 4 ? 'border-white bg-white text-blue-600' : 'border-blue-300'
              }`}>
                4
              </div>
              <span className="ml-2 text-sm">Generate</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 180px)" }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>

        {/* Footer - only show for step 1 */}
        {step === 1 && (
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-between">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAnalyzeRFP}
                disabled={!formData.documentName || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Analyze RFP & Continue
                    <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateDocumentModal;